/**
 * Centralized Form Field Component
 * Provides consistent form field rendering with validation
 */

import React, {useState} from 'react';
import AppInput from '../ui/AppInput';
import AppSelect from '../ui/AppSelect';
import AppTextarea from '../ui/AppTextarea';
import AppCheckbox from '../ui/AppCheckbox';
import AppFileInput from '../ui/AppFileInput';
import {FormFieldProps} from '../../utils/formUtils';

export interface FormFieldComponentProps extends FormFieldProps {
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'textarea' | 'select' | 'checkbox' | 'file';
  label?: string;
  placeholder?: string;
  options?: Array<{ value: any; label: string }>;
  multiple?: boolean;
  accept?: string;
  rows?: number;
  min?: number;
  max?: number;
  step?: number;
  autoFocus?: boolean;
  className?: string;
}

const FormField: React.FC<FormFieldComponentProps> = ({
  type = 'text',
  label,
  placeholder,
  value,
  onChange,
  error,
  disabled,
  options = [],
  multiple = false,
  accept,
  rows = 3,
  min,
  max,
  step,
  autoFocus = false,
  className = ''
}) => {
  const commonProps = {
    label,
    value,
    onChange,
    error,
    disabled,
    autoFocus,
    className
  };

  switch (type) {
    case 'textarea':
      return (
        <AppTextarea
          {...commonProps}
          placeholder={placeholder}
          rows={rows}
        />
      );

    case 'select':
      return (
        <AppSelect
          {...commonProps}
          options={options}
          multiple={multiple}
        />
      );

    case 'checkbox':
      return (
        <AppCheckbox
          {...commonProps}
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
        />
      );

    case 'file':
      return (
        <AppFileInput
          {...commonProps}
          accept={accept}
          multiple={multiple}
        />
      );

    case 'number':
      return (
        <AppInput
          {...commonProps}
          type="number"
          placeholder={placeholder}
          min={min}
          max={max}
          step={step}
        />
      );

    case 'email':
      return (
        <AppInput
          {...commonProps}
          type="email"
          placeholder={placeholder}
        />
      );

    case 'password':
      return <PasswordField {...commonProps} placeholder={placeholder} />;

    case 'tel':
      return (
        <AppInput
          {...commonProps}
          type="tel"
          placeholder={placeholder}
        />
      );

    case 'text':
    default:
      return (
        <AppInput
          {...commonProps}
          type="text"
          placeholder={placeholder}
        />
      );
  }
};

/**
 * Password Field Component with Show/Hide Toggle
 */
const PasswordField: React.FC<{
  label?: string;
  value?: string;
  onChange?: (value: any) => void;
  error?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
  placeholder?: string;
}> = ({
  label,
  value,
  onChange,
  error,
  disabled,
  autoFocus,
  className = '',
  placeholder
}) => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="w-full">
      {label && (
        <label className="block mb-2 font-bold text-gray-700">
          {label}
        </label>
      )}
      <div className="relative">
        <AppInput
          value={value}
          onChange={onChange}
          error={error}
          disabled={disabled}
          autoFocus={autoFocus}
          type={showPassword ? 'text' : 'password'}
          placeholder={placeholder}
          className={`${className} pr-10`}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none z-10"
          tabIndex={-1}
          aria-label={showPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
        >
          {showPassword ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
};

export default FormField;
